// Partial store, imported into customizer.

export default {
    async validateGroupPersonalization() {
        // Nothing to do if no group personalization enabled.
        if (this.groupPersonalization == null) {
            return;
        }

        const targetType = this.targetGroupPersonalizationType;
        const currentType = this.currentGroupPersonalizationType;

        let isSame = false;

        if (targetType?.type === currentType?.type && targetType?.code === currentType?.code) {
            isSame = true;
        }

        // Need to rebuild groups and stuff.
        if (!isSame) {
            this.currentGroupPersonalizationType = targetType;

            if (targetType == null) {
                this.groupPersonalization = null;
            }

            if (targetType?.type === 'text') {
                const selected = this.viewController.findComponent(targetType.code);

                // Step one... Do we have enough groups?
                if (this.groupPersonalization.length < 1) {
                    this.groupPersonalization.push({
                        type: 'text',
                        text: selected.custom['personalization-text'],
                        // This must have been validated somewhere already.
                        valid: true,
                    });

                    this.selectedGroupItem = this.groupPersonalization[0];
                }

                // Validate allowed length.
                const maxCharacters = +(selected?.custom['personalization-limit'] ?? '0');

                for (const group of this.groupPersonalization) {
                    group.type = 'text';

                    if (group.text.length > maxCharacters) {
                        group.text = group.text.substr(0, maxCharacters);
                    }

                    // Validate text.
                    if (group.valid) {
                        // eslint-disable-next-line no-await-in-loop
                        const result = await this.viewController.updatePersonalization('Text', group.text, {
                            preview: true,
                        });

                        if (result.accepted) {
                            if (result.acceptedAs) {
                                group.text = result.acceptedAs;
                            }
                        } else {
                            group.valid = false;
                        }
                    }
                }
            }

            if (targetType?.type === 'monogram') {
                const selected = this.viewController.findComponent(targetType.code);

                // Step one... Do we have enough groups?
                if (this.groupPersonalization.length < 1) {
                    this.groupPersonalization.push({
                        type: 'monogram',
                        text: selected.custom['personalization-text'],
                        // This must have been validated somewhere already.
                        valid: true,
                    });

                    this.selectedGroupItem = this.groupPersonalization[0];
                }

                const minCharacters = +(selected?.custom?.['personalization-limit-min'] ?? '3');
                const maxCharacters = +(selected?.custom?.['personalization-limit-max'] ?? '3');

                for (const group of this.groupPersonalization) {
                    group.type = 'monogram';

                    if (group.text.length > maxCharacters) {
                        group.text = group.text.substr(0, maxCharacters);
                    }

                    if (group.text.length < minCharacters) {
                        group.valid = false;
                    }

                    if (group.valid) {
                        // Validate text.
                        // eslint-disable-next-line no-await-in-loop
                        const result = await this.viewController.updatePersonalization('Monogram', group.text, {
                            preview: true,
                        });

                        if (result.accepted) {
                            if (result.acceptedAs) {
                                group.text = result.acceptedAs;
                            }
                        } else {
                            group.valid = false;
                        }
                    }
                }
            }
        }
    },

    disableGroupPersonalization() {
        // If currently enabled...
        // Select the first base item...
        // Sync it to controller...
        // And only then disable everything.

        if (this.groupPersonalization != null) {
            this.selectedGroupItem = this.groupPersonalization[0];
            this.syncGroupPersonalization();
        }

        this.groupPersonalization = null;
        this.selectedGroupItem = null;
        this.currentGroupPersonalizationType = null;
    },

    enableGroupPersonalization() {
        if (this.targetGroupPersonalizationType != null) {
            if (this.groupPersonalization == null) {
                this.quantity = 1;

                this.groupPersonalization = [];

                this.validateGroupPersonalization();
            }
        }
    },

    async updateGroupQuantity(count) {
        await this.validateGroupPersonalization();

        while (this.groupPersonalization.length > count) {
            this.groupPersonalization.pop();
        }

        while (this.groupPersonalization.length < count) {
            this.groupPersonalization.push({
                type: this.groupPersonalization[0].type,

                text: '',

                valid: false,
                justAdded: true,
            });
        }

        const selectedIndex = this.groupPersonalization.indexOf(this.selectedGroupItem);
        if (selectedIndex < 0) {
            this.selectedGroupItem = this.groupPersonalization[this.groupPersonalization.length - 1];
        }
    },

    async applyGroupPersonalizationList(list) {
        const notEmpty = list.map((x) => x.trim()).filter((x) => x.length > 0);

        if (notEmpty.length > 0) {
            // First ensure the proper count.
            await this.updateGroupQuantity(notEmpty.length);

            // Then apply personalization for each.
            // Leave in unvalidated status.

            for (let i = 0; i < notEmpty.length; i += 1) {
                this.groupPersonalization[i].text = notEmpty[i];

                this.groupPersonalization[i].justAdded = false;

                if (this.groupPersonalization[i].type === 'monogram') {
                    this.groupPersonalization[i].valid = true;
                }
            }

            this.syncGroupPersonalization();
        }
    },

    selectGroupItem(item) {
        this.selectedGroupItem = item;

        this.syncGroupPersonalization();
    },

    /**
     * Synchronize selected group personalization item with the controller.
     */
    syncGroupPersonalization(item) {
        const useItem = item ?? this.selectedGroupItem;

        if (useItem) {
            if (useItem.type === 'text') {
                const text = useItem.valid ? useItem.text : '';

                this.viewController.updatePersonalization('Text', text);

                this.clearPlacement('Monogram');

                this.viewController.clearPlacementTags('Text');

                if (text.length > 0) {
                    this.viewController.addPlacementTag('Text', 'personalized');
                }

                this.viewController.updateComponents();
            }

            if (useItem.type === 'monogram') {
                const text = useItem.valid ? useItem.text : '';

                this.viewController.updatePersonalization('Monogram', text);

                this.clearPlacement('Text');

                this.viewController.clearPlacementTags('Monogram');

                if (text.length > 0) {
                    this.viewController.addPlacementTag('Monogram', 'personalized');
                }

                this.viewController.updateComponents();
            }
        }
    },

    updateGroupPersonalization(item, text) {
        item.valid = true;
        item.justAdded = false;

        if (item.text !== text) {
            item.text = text;

            if (item === this.selectedGroupItem) {
                this.syncGroupPersonalization();
            }
        }
    },

    setGroupPersonalizationError(item, status) {
        item.valid = !status;
    },

    removeGroupItem(item) {
        if (this.groupPersonalization) {
            const index = this.groupPersonalization.indexOf(item);
            if (index >= 0) {
                this.groupPersonalization.splice(index, 1);

                if (this.selectedGroupItem === item) {
                    if (index < this.groupPersonalization.length) {
                        this.selectedGroupItem = this.groupPersonalization[index];
                    } else {
                        this.selectedGroupItem = this.groupPersonalization[this.groupPersonalization.length - 1];
                    }
                }
            }
        }
    },
};
